import { FaroButton, FaroDialog, FaroOverlayModal, FaroOverlayModalProps, FaroOverlayRef } from '@faro/design-system';
import { FaroSelectField } from '@faro/forms';
import { Formik } from 'formik';
import { ReactNode } from 'react';
import { useServiceContext } from '../../../context';
import styles from './StudyDefinitionMoveModal.module.scss';
import { useStudyDefinitionMoveModalData } from './useStudyDefinitionMoveModalData';

export interface StudyDefinitionMoveModalProps extends Omit<FaroOverlayModalProps, 'showCloseButton'> {
    fromSpaceId: string;
    onMove: (toSpaceId: string) => Promise<any> | void;
    hasChildren?: boolean;
}

export interface StudyDefinitionMoveFormValues {
    spaceId: string;
}

function StudyDefinitionMoveModal(props: StudyDefinitionMoveModalProps): ReactNode {
    const { onMove, fromSpaceId, hasChildren = false, ref, ...rest } = props;
    const overlayRef = rest.overlayRef as FaroOverlayRef;

    function onSubmit(values: StudyDefinitionMoveFormValues): void {
        onMove(values.spaceId);
    }

    const initialValues = { spaceId: fromSpaceId };

    const context = useServiceContext();

    const [resource] = useStudyDefinitionMoveModalData({ context, query: undefined });
    const spacesOptions = resource.data?.spaces.map(space => ({
        label: space.title || 'Untitled',
        value: space.id,
    }));
    if (resource.status === 'loading' || resource.data == null) {
        return null;
    }

    return (
        <FaroOverlayModal ref={ref} {...rest}>
            <FaroDialog data-automation-id="moveStudyDefinitionModal">
                <Formik initialValues={initialValues} onSubmit={onSubmit}>
                    {({ submitForm, dirty }) => {
                        return (
                            <>
                                <FaroDialog.Header>Move</FaroDialog.Header>
                                <FaroDialog.Body>
                                    {`Choose a study space to move this definition ${hasChildren ? 'and all substudies' : ''}
                                    to`}
                                    <FaroSelectField
                                        name="spaceId"
                                        options={spacesOptions}
                                        className={styles.select}
                                        clearable={false}
                                        fullWidth
                                        data-automation-id="selectStudySpace"
                                        data-testid="selectStudySpace"
                                    />
                                </FaroDialog.Body>
                                <FaroDialog.Controls>
                                    <FaroButton
                                        appearance="inline"
                                        onClick={() => {
                                            overlayRef.close();
                                        }}
                                        data-automation-id="cancelButton"
                                    >
                                        Cancel
                                    </FaroButton>
                                    <FaroButton
                                        color="primary"
                                        onClick={() => {
                                            submitForm();
                                            overlayRef.close();
                                        }}
                                        disabled={!dirty || !spacesOptions?.length || spacesOptions.length <= 1}
                                        data-automation-id="moveButton"
                                        data-testid="moveButton"
                                    >
                                        Move
                                    </FaroButton>
                                </FaroDialog.Controls>
                            </>
                        );
                    }}
                </Formik>
            </FaroDialog>
        </FaroOverlayModal>
    );
}

export default StudyDefinitionMoveModal;
